import {AxiosError} from 'axios'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {UserExtended} from '@/features/users/types'
import {remapUserToDefaultValues} from '@/features/users/utils'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {FormErrorResponse} from '@/types'
import {useScrollContext} from '@/hooks/useScrollContext'
import {z, ZodSchema} from 'zod'

export const useUserFormSubmission = ({formSchema}: {formSchema: ZodSchema}) => {
    const {t} = useTranslation()
    const form = useFormContext<z.infer<typeof formSchema>>()
    const setEditingFieldsGroup = useUserDetailsStore(state => state.setEditingFieldsGroup)
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const isErrorBannerVisible = useUserDetailsStore(state => state.isErrorBannerVisible)
    const isContractTypeModalVisible = useUserDetailsStore(state => state.isContractTypeModalVisible)
    const setIsContractTypeModalVisible = useUserDetailsStore(state => state.setIsContractTypeModalVisible)

    const {scrollToTop} = useScrollContext()

    const onSuccessSubmit = (response: UserExtended) => {
        if (isContractTypeModalVisible) {
            setIsContractTypeModalVisible(false)
        }

        if (isErrorBannerVisible) {
            setIsErrorBannerVisible(false)
        }

        form.reset(remapUserToDefaultValues(response))
        toast.success(t('commons:update_success'))
        setEditingFieldsGroup(null)
        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS]})
    }

    const onErrorSubmit = (errors: AxiosError<{errors: FormErrorResponse[]}>) => {
        if (errors?.response?.data.errors) {
            setIsErrorBannerVisible(true)
            errors.response.data.errors.forEach(error => {
                if (error.field == 'email') {
                    form.setError('email', {
                        message: t('errors:email_already_exists'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'regex') {
                    form.setError('taxCode', {
                        message: t('errors:invalid_tax_code'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'database.exists') {
                    form.setError('taxCode', {
                        message: t('errors:tax_code_already_exists'),
                        type: 'custom'
                    })
                }

                if (error.field == 'iban') {
                    form.setError('iban', {
                        message: t('errors:iban'),
                        type: 'custom'
                    })
                }

                if (error.field == 'phoneNumber') {
                    form.setError('phoneNumber', {
                        message: t('errors:phone_number'),
                        type: 'custom'
                    })
                }
            })

            scrollToTop()
        }
    }

    return {
        onSuccessSubmit,
        onErrorSubmit,
        scrollToTop
    }
}
